import { formatDateTime } from "@/lib/format";
import { glopicApi } from "@/lib/glopic-api";
import Pagination from "@/components/base/Pagination";
import FilterBar from "@/components/base/FilterBar";
import UserIdentityCell from "@/components/base/UserIdentityCell";
import { buildSubscriptionOrderFilters } from "./contracts";

interface SubscriptionOrderRow {
  order_id: string;
  user_id: string;
  user_type: "consumer" | "merchant";
  nickname: string | null;
  avatar: string | null;
  product_type: "vip_subscription";
  package_id: string;
  package_name: string | null;
  amount: number;
  currency: string;
  pay_channel: string;
  payment_status: number;
  payment_status_label: string;
  payment_failure_code: string | null;
  payment_failure_label: string | null;
  order_kind: "initial" | "renewal";
  order_kind_label: string;
  apple_recovery_state: string | null;
  superseded_by_order_id: string | null;
  fulfillment_status: "pending" | "fulfilled" | "failed";
  period_result: "pending" | "success" | "failed" | "refunded";
  period_result_label: string;
  period_start_at: string | null;
  period_end_at: string | null;
  period_end_source: "order" | "first_renewal" | "subscription" | "derived_duration" | "unavailable";
  points_granted: number;
  paid_at: string | null;
  order_created_at: string;
  subscription_id: string | null;
  subscription_status: number | null;
  subscription_status_label: string;
  subscription_start_time: string | null;
  subscription_expires_at: string | null;
  current_subscription_status: number | null;
  current_subscription_status_label: string;
  current_subscription_start_at: string | null;
  current_subscription_expires_at: string | null;
  subscription_provider: string | null;
  auto_renew_status: string | null;
  next_renew_time: string | null;
  user_is_vip: boolean;
  user_vip_expire_time: string | null;
  alignment_status: "aligned" | "warning" | "blocked";
  alignment_issues: string[];
  is_subscription_success: boolean;
  abnormal_reason: string | null;
}

interface PaidAmountSummary {
  currency: string;
  paid_orders: number;
  paid_amount: number;
}

const paymentStatusOptions = [
  { value: "0", label: "待支付" },
  { value: "1", label: "已支付" },
  { value: "2", label: "支付失败" },
  { value: "3", label: "已取消" },
  { value: "4", label: "已退款" },
];

const subscriptionStatusOptions = [
  { value: "0", label: "未开通" },
  { value: "1", label: "已开通" },
  { value: "2", label: "已取消" },
  { value: "3", label: "已过期" },
  { value: "4", label: "宽限期" },
];

function paymentStatusClass(status: number) {
  if (status === 1) return "bg-emerald-500/10 text-emerald-600";
  if (status === 0) return "bg-amber-500/10 text-amber-600";
  if (status === 4) return "bg-blue-500/10 text-blue-600";
  if (status === 2 || status === 3) return "bg-red-500/10 text-red-600";
  return "bg-text-muted/10 text-text-muted";
}

function subscriptionStatusClass(row: SubscriptionOrderRow) {
  if (row.period_result === "success") return "bg-emerald-500/10 text-emerald-600";
  if (row.period_result === "pending") return "bg-amber-500/10 text-amber-600";
  if (row.period_result === "refunded") return "bg-blue-500/10 text-blue-600";
  if (row.abnormal_reason) return "bg-red-500/10 text-red-600";
  if (row.subscription_status === 4) return "bg-amber-500/10 text-amber-600";
  return "bg-text-muted/10 text-text-muted";
}

function paymentFailureText(row: SubscriptionOrderRow) {
  if (row.payment_status !== 2) return "";
  return row.payment_failure_label || "支付失败原因待分类";
}

const periodEndSourceLabels: Record<SubscriptionOrderRow["period_end_source"], string> = {
  order: "订单记录",
  first_renewal: "首笔续费开始时间",
  subscription: "当前订阅记录",
  derived_duration: "套餐期限推导",
  unavailable: "支付未确认",
};

const alignmentIssueLabels: Record<string, string> = {
  paid_not_fulfilled: "已支付但权益未履约",
  unpaid_but_fulfilled: "未支付订单被标记为已履约",
  stale_active_subscription: "订阅已到期但状态仍有效",
  future_entitlement_marked_expired: "权益未到期但订阅状态已过期",
  user_vip_projection_mismatch: "用户 VIP 状态与订阅权益不一致",
  user_vip_expiry_invalid: "用户 VIP 到期时间无效",
  user_vip_expiry_mismatch: "用户 VIP 到期时间与当前权益不一致",
  manual_entitlement_on_unconfirmed_order: "支付未确认，当前权益来自人工支持",
  apple_subscription_pending_reconciliation: "Apple 自动续费状态等待对账",
};

const recoveryStateLabels: Record<string, string> = {
  awaiting_transaction: "等待Apple交易",
  verified_unfulfilled: "已确认支付，权益恢复中",
  recovered: "已恢复",
  superseded: "重复订单已替代",
  manual_review: "待人工核对",
  no_provider_evidence: "未发现Apple交易",
};

function currencySymbol(currency?: string | null) {
  const normalized = (currency || "").toUpperCase();
  if (normalized === "USD") return "$";
  if (normalized === "CNY" || normalized === "RMB") return "¥";
  return "";
}

function formatCurrencyAmount(amount: number | string | null | undefined, currency?: string | null) {
  const normalized = (currency || "").toUpperCase();
  const symbol = currencySymbol(normalized);
  const formatted = Number(amount || 0).toFixed(2);
  return `${symbol}${formatted}${normalized ? ` ${normalized}` : ""}`;
}

function formatPaidSummary(items: PaidAmountSummary[]) {
  if (!items.length) return "¥0.00 CNY / $0.00 USD";
  return items
    .map((item) => formatCurrencyAmount(item.paid_amount, item.currency))
    .join(" / ");
}

function DetailLine({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div>
      <p className="text-[11px] text-text-muted">{label}</p>
      <p className="mt-1 text-sm text-text-primary break-all">{value || "-"}</p>
    </div>
  );
}

export default function AiSubscriptionOrdersPage() {
  const [orders, setOrders] = useState<SubscriptionOrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState("");
  const [userType, setUserType] = useState("");
  const [orderKind, setOrderKind] = useState("");
  const [fulfillmentStatus, setFulfillmentStatus] = useState("");
  const [autoRenewStatus, setAutoRenewStatus] = useState("");
  const [alignmentStatus, setAlignmentStatus] = useState("");
  const [membershipView, setMembershipView] = useState("");
  const [abnormalOnly, setAbnormalOnly] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState<SubscriptionOrderRow | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [subscriptionSuccessTotal, setSubscriptionSuccessTotal] = useState(0);
  const [activeSubscriptionChains, setActiveSubscriptionChains] = useState(0);
  const [abnormalTotal, setAbnormalTotal] = useState(0);
  const [paidAmountByCurrency, setPaidAmountByCurrency] = useState<PaidAmountSummary[]>([]);
  const [pageSize, setPageSize] = useState(10);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        ...buildSubscriptionOrderFilters({
          search,
          paymentStatus,
          subscriptionStatus,
          userType,
          orderKind,
          fulfillmentStatus,
          autoRenewStatus,
          alignmentStatus,
          membershipView,
          abnormalOnly,
        }),
        page,
        page_size: pageSize,
      };

      const response = await glopicApi.get("/subscription-orders", { params });
      const data = response.data.data;
      setOrders(data.items ?? []);
      setTotal(data.total ?? 0);
      setSubscriptionSuccessTotal(data.subscription_success_total ?? 0);
      setActiveSubscriptionChains(data.active_subscription_chains ?? 0);
      setAbnormalTotal(data.abnormal_total ?? 0);
      setPaidAmountByCurrency(data.paid_amount_by_currency ?? []);
    } catch (err) {
      console.error("Fetch subscription orders error:", err);
    } finally {
      setLoading(false);
    }
  }, [abnormalOnly, alignmentStatus, autoRenewStatus, fulfillmentStatus, membershipView, orderKind, page, pageSize, paymentStatus, search, subscriptionStatus, userType]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const totalPages = Math.ceil(total / pageSize);
  const paidAmountSummary = formatPaidSummary(paidAmountByCurrency);

  const exportOrders = async () => {
    setExporting(true);
    try {
      const params = buildSubscriptionOrderFilters({
        search,
        paymentStatus,
        subscriptionStatus,
        userType,
        orderKind,
        fulfillmentStatus,
        autoRenewStatus,
        alignmentStatus,
        membershipView,
        abnormalOnly,
      });
      const response = await glopicApi.get("/subscription-orders/export", {
        params,
        responseType: "blob",
      });
      downloadBlob(response.data, `glowpic-orders-${userType || "all"}.csv`);
    } catch (err) {
      console.error("Export subscription orders error:", err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">订阅订单</h2>
          <p className="text-text-secondary text-sm mt-1">
            共 {total} 个周期 · 成功周期 {subscriptionSuccessTotal} 个 · 活跃订阅链 {activeSubscriptionChains} 条 · 已支付总额 {paidAmountSummary}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void exportOrders()}
            disabled={exporting}
            className="h-9 px-3 rounded-lg border border-surface-border text-sm text-text-secondary hover:bg-surface disabled:opacity-50"
          >
            <i className="ri-download-line mr-1" />
            {exporting ? "导出中..." : "导出当前筛选"}
          </button>
          <button
            onClick={() => void fetchOrders()}
            className="h-9 px-3 rounded-lg border border-surface-border text-sm text-text-secondary hover:bg-surface transition-colors cursor-pointer"
          >
            刷新
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3">
          <p className="text-[11px] text-text-muted">当前页订单</p>
          <p className="text-lg font-semibold text-text-primary mt-1">{orders.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3">
          <p className="text-[11px] text-text-muted">成功周期</p>
          <p className="text-lg font-semibold text-emerald-600 mt-1">{subscriptionSuccessTotal}</p>
        </div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3">
          <p className="text-[11px] text-text-muted">已支付总额</p>
          <p className="text-lg font-semibold text-text-primary mt-1">{paidAmountSummary}</p>
        </div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3">
          <p className="text-[11px] text-text-muted">异常周期</p>
          <p className={`text-lg font-semibold mt-1 ${abnormalTotal > 0 ? "text-red-600" : "text-text-primary"}`}>
            {abnormalTotal}
          </p>
        </div>
      </div>

      <FilterBar
        search={search}
        onSearchChange={(val) => { setSearch(val); setPage(1); }}
        searchPlaceholder="搜索订单号、用户ID或昵称..."
        filters={[
          {
            key: "order_kind",
            label: "全部周期类型",
            type: "select",
            options: [
              { value: "initial", label: "首购" },
              { value: "renewal", label: "续费" },
            ],
          },
          {
            key: "fulfillment_status",
            label: "全部履约状态",
            type: "select",
            options: [
              { value: "pending", label: "处理中" },
              { value: "fulfilled", label: "已履约" },
              { value: "failed", label: "履约失败" },
            ],
          },
          {
            key: "payment_status",
            label: "全部支付状态",
            type: "select",
            options: paymentStatusOptions,
          },
          {
            key: "subscription_status",
            label: "全部订阅状态",
            type: "select",
            options: subscriptionStatusOptions,
          },
          {
            key: "user_type",
            label: "全部用户类型",
            type: "select",
            options: [
              { value: "consumer", label: "C端用户" },
              { value: "merchant", label: "商家用户" },
            ],
          },
          {
            key: "membership_view",
            label: "全部会员视图",
            type: "select",
            options: [
              { value: "effective", label: "有效会员" },
              { value: "expired", label: "已过期" },
              { value: "auto_renew", label: "自动续费" },
              { value: "renewal_off", label: "续费关闭" },
              { value: "apple_pending", label: "Apple待恢复" },
              { value: "manual", label: "人工权益" },
              { value: "product_conflict", label: "商品冲突" },
            ],
          },
          {
            key: "alignment_status",
            label: "全部对齐状态",
            type: "select",
            options: [
              { value: "aligned", label: "已对齐" },
              { value: "warning", label: "需关注" },
              { value: "blocked", label: "状态不一致" },
            ],
          },
          {
            key: "auto_renew_status",
            label: "全部续费状态",
            type: "select",
            options: [
              { value: "enabled", label: "续费开启" },
              { value: "disabled", label: "续费关闭" },
              { value: "not_supported", label: "待兼容对账" },
              { value: "unknown", label: "未知" },
            ],
          },
          {
            key: "abnormal_only",
            label: "全部订单",
            type: "select",
            options: [{ value: "1", label: "只看异常" }],
          },
        ]}
        filterValues={{
          payment_status: paymentStatus,
          subscription_status: subscriptionStatus,
          user_type: userType,
          order_kind: orderKind,
          fulfillment_status: fulfillmentStatus,
          abnormal_only: abnormalOnly ? "1" : "",
          auto_renew_status: autoRenewStatus,
          alignment_status: alignmentStatus,
          membership_view: membershipView,
        }}
        onFilterChange={(key, val) => {
          if (key === "payment_status") setPaymentStatus(val);
          if (key === "subscription_status") setSubscriptionStatus(val);
          if (key === "user_type") setUserType(val);
          if (key === "order_kind") setOrderKind(val);
          if (key === "fulfillment_status") setFulfillmentStatus(val);
          if (key === "abnormal_only") setAbnormalOnly(val === "1");
          if (key === "auto_renew_status") setAutoRenewStatus(val);
          if (key === "alignment_status") setAlignmentStatus(val);
          if (key === "membership_view") setMembershipView(val);
          setPage(1);
        }}
        onReset={() => {
          setSearch("");
          setPaymentStatus("");
          setSubscriptionStatus("");
          setUserType("");
          setOrderKind("");
          setFulfillmentStatus("");
          setAbnormalOnly(false);
          setAutoRenewStatus("");
          setAlignmentStatus("");
          setMembershipView("");
          setPage(1);
        }}
      />

      <div className="bg-white rounded-xl border border-surface-border overflow-hidden">
        <div className="overflow-x-auto -mx-0">
          <table className="w-full text-sm min-w-[1180px]">
            <thead>
              <tr className="border-b border-surface-border bg-surface/50">
                <th className="text-left px-4 py-3.5 text-text-secondary font-medium text-xs">订单号</th>
                <th className="text-left px-4 py-3.5 text-text-secondary font-medium text-xs">用户</th>
                <th className="text-left px-4 py-3.5 text-text-secondary font-medium text-xs">套餐</th>
                <th className="text-left px-4 py-3.5 text-text-secondary font-medium text-xs">订阅金额</th>
                <th className="text-left px-4 py-3.5 text-text-secondary font-medium text-xs">支付状态</th>
                <th className="text-left px-4 py-3.5 text-text-secondary font-medium text-xs">订阅状态</th>
                <th className="text-left px-4 py-3.5 text-text-secondary font-medium text-xs">支付时间</th>
                <th className="text-left px-4 py-3.5 text-text-secondary font-medium text-xs">订阅到期</th>
                <th className="text-right px-4 py-3.5 text-text-secondary font-medium text-xs">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-5 py-12 text-center text-text-muted">
                    <i className="ri-loader-4-line animate-spin text-2xl mb-2 block" />
                    加载中...
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-5 py-12 text-center text-text-muted">
                    <i className="ri-bill-line text-3xl mb-2 block" />
                    暂无订阅订单
                  </td>
                </tr>
              ) : (
                orders.map((o) => (
                  <tr
                    key={o.order_id}
                    className={`border-b border-surface-border hover:bg-surface/30 transition-colors ${o.alignment_status === "blocked" ? "bg-red-50/60" : o.alignment_status === "warning" ? "bg-amber-50/50" : ""}`}
                  >
                    <td className="px-4 py-4">
                      <p className="text-text-primary text-xs font-mono">{o.order_id}</p>
                      <p className="text-text-muted text-[10px] mt-1">{o.pay_channel}</p>
                    </td>
                    <td className="px-4 py-4">
                      <UserIdentityCell nickname={o.nickname} userId={o.user_id} avatar={o.avatar} />
                      <span className={`mt-1 inline-flex px-1.5 py-0.5 rounded text-[10px] ${o.user_type === "merchant" ? "bg-violet-500/10 text-violet-600" : "bg-blue-500/10 text-blue-600"}`}>
                        {o.user_type === "merchant" ? "商家用户" : "C端用户"}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-text-secondary text-xs">
                      <p>{o.package_name || o.package_id}</p>
                      <span className="mt-1 inline-flex rounded bg-violet-500/10 px-1.5 py-0.5 text-[10px] text-violet-600">
                        {o.order_kind_label}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-text-primary font-semibold">{formatCurrencyAmount(o.amount, o.currency)}</span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="space-y-1">
                        <span className={`inline-flex px-2 py-1 rounded-full text-[11px] font-medium ${paymentStatusClass(o.payment_status)}`}>
                          {o.payment_status_label}
                        </span>
                        {paymentFailureText(o) && (
                          <p className="max-w-[150px] text-[10px] leading-4 text-red-600">
                            {paymentFailureText(o)}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="space-y-1">
                        <span className={`inline-flex px-2 py-1 rounded-full text-[11px] font-medium ${subscriptionStatusClass(o)}`}>
                          {o.period_result_label}
                        </span>
                        {o.abnormal_reason && (
                          <p className="text-[10px] text-red-600">{o.abnormal_reason}</p>
                        )}
                        {o.alignment_status !== "aligned" && (
                          <p className={`text-[10px] ${o.alignment_status === "blocked" ? "text-red-600" : "text-amber-600"}`}>
                            {o.alignment_status === "blocked" ? "状态不一致" : "需关注"}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-text-muted text-xs">
                      {formatDateTime(o.paid_at || o.order_created_at)}
                    </td>
                    <td className="px-4 py-4 text-text-muted text-xs">
                      <p>{o.period_end_at ? formatDateTime(o.period_end_at) : "支付未确认"}</p>
                      <p className="mt-1 text-[10px] text-text-muted">{periodEndSourceLabels[o.period_end_source]}</p>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="inline-flex gap-3">
                        <button
                          onClick={() => setSelected(o)}
                          className="text-brand text-xs hover:underline cursor-pointer"
                        >
                          查看详情
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/20" onClick={() => setSelected(null)}>
          <aside
            className="h-full w-full max-w-[560px] bg-white shadow-xl overflow-y-auto"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border">
              <div>
                <h3 className="text-base font-semibold text-text-primary">订阅订单详情</h3>
                <p className="text-xs text-text-muted mt-1">{selected.order_id}</p>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="h-8 w-8 rounded-lg border border-surface-border text-text-secondary hover:bg-surface cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {selected.abnormal_reason && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {selected.abnormal_reason}
                </div>
              )}
              {selected.alignment_issues.length > 0 && (
                <div className={`rounded-lg border px-4 py-3 text-sm ${selected.alignment_status === "blocked" ? "border-red-200 bg-red-50 text-red-600" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
                  {selected.alignment_issues.map((issue) => (
                    <p key={issue}>{alignmentIssueLabels[issue] || issue}</p>
                  ))}
                </div>
              )}

              <section className="space-y-3">
                <h4 className="text-sm font-semibold text-text-primary">用户信息</h4>
                <UserIdentityCell nickname={selected.nickname} userId={selected.user_id} avatar={selected.avatar} />
                <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] ${selected.user_type === "merchant" ? "bg-violet-500/10 text-violet-600" : "bg-blue-500/10 text-blue-600"}`}>
                  {selected.user_type === "merchant" ? "商家用户" : "C端用户"}
                </span>
              </section>

              <section className="space-y-3">
                <h4 className="text-sm font-semibold text-text-primary">订单信息</h4>
                <div className="grid grid-cols-2 gap-4">
                  <DetailLine label="订单号" value={selected.order_id} />
                  <DetailLine label="支付渠道" value={selected.pay_channel} />
                  <DetailLine label="订阅金额" value={formatCurrencyAmount(selected.amount, selected.currency)} />
                  <DetailLine label="支付状态" value={selected.payment_status_label} />
                  <DetailLine label="周期类型" value={selected.order_kind_label} />
                  <DetailLine
                    label="恢复状态"
                    value={
                      selected.apple_recovery_state
                        ? recoveryStateLabels[selected.apple_recovery_state] || "待人工核对"
                        : null
                    }
                  />
                  <DetailLine label="履约状态" value={selected.fulfillment_status} />
                  <DetailLine label="赠送积分" value={selected.points_granted} />
                  <DetailLine label="失败类型" value={paymentFailureText(selected)} />
                  <DetailLine label="下单时间" value={formatDateTime(selected.order_created_at)} />
                  <DetailLine label="支付时间" value={formatDateTime(selected.paid_at)} />
                </div>
              </section>

              <section className="space-y-3">
                <h4 className="text-sm font-semibold text-text-primary">订阅信息</h4>
                <div className="grid grid-cols-2 gap-4">
                  <DetailLine label="套餐" value={selected.package_name || selected.package_id} />
                  <DetailLine label="周期结果" value={selected.period_result_label} />
                  <DetailLine label="订阅ID" value={selected.subscription_id} />
                  <DetailLine label="周期开始" value={formatDateTime(selected.period_start_at)} />
                  <DetailLine label="周期结束" value={formatDateTime(selected.period_end_at)} />
                  <DetailLine label="周期结束来源" value={periodEndSourceLabels[selected.period_end_source]} />
                  <DetailLine label="当前订阅状态" value={selected.current_subscription_status_label} />
                  <DetailLine label="当前订阅开始" value={formatDateTime(selected.current_subscription_start_at)} />
                  <DetailLine label="当前订阅到期" value={formatDateTime(selected.current_subscription_expires_at)} />
                  <DetailLine label="下一续费" value={formatDateTime(selected.next_renew_time)} />
                  <DetailLine label="Provider" value={selected.subscription_provider} />
                  <DetailLine label="自动续费状态" value={selected.auto_renew_status} />
                  <DetailLine label="用户 VIP 投影" value={selected.user_is_vip ? "有效" : "无效"} />
                  <DetailLine label="用户 VIP 到期" value={formatDateTime(selected.user_vip_expire_time)} />
                  <DetailLine label="对齐状态" value={selected.alignment_status} />
                </div>
              </section>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function downloadBlob(data: string | Blob | ArrayBuffer, filename: string) {
  const url = URL.createObjectURL(new Blob([data], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
